// StreamAudioPlayer.js
import { Howl, Howler } from 'howler';
// config.js
export const config = {
  ttsEndpoint: 'YOUR_TTS_API_ENDPOINT',
  formats: ['wav'],
  silence: 'data:audio/wav;base64,UklGRlwAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAAABmYWN0BAAAABAAAAAA',
  initialVolume: 1
};

// audioUtils.js
export const detectBestFormat = () => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'mp3';
  if (/Android/.test(ua)) return 'ogg';
  return 'mp3';
};

const isIOSUserAgent = () => /iPhone|iPad|iPod/.test(navigator.userAgent)
const isAndroidUserAgent = () => /Android/.test(navigator.userAgent)
// iOS 继续走 html5 audio 以兼容自动播放策略；Android 优先走 WebAudio，避免音频路由频繁漂移。
const shouldUseHtml5Audio = () => isIOSUserAgent()

function base64ToObjectUrl(base64, mimeType = 'audio/wav') {
  const binary = window.atob(base64);
  const length = binary.length;
  const bytes = new Uint8Array(length);
  for (let i = 0; i < length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return URL.createObjectURL(new Blob([bytes], { type: mimeType }));
}

export const initMobileHandlers = () => {
  if (!shouldUseHtml5Audio()) return;

  Howler.autoUnlock = true;
  Howler.usingWebAudio = false;
  
  document.body.addEventListener('touchstart', () => {
    Howler.ctx?.resume?.();
  });
};

let unlockWebAudioPromise = null;
let globalAudioReadyCleanup = null;
const CHAT_AUDIO_UNLOCKED_KEY = 'chat_audio_unlocked';
let stablePlaybackMuted = false;
let stablePlaybackVolume = 1;
const stablePlaybackGainNodes = new Set();

function syncStablePlaybackGains() {
  stablePlaybackGainNodes.forEach((gainNode) => {
    if (!gainNode) return;
    gainNode.gain.value = stablePlaybackMuted ? 0 : stablePlaybackVolume;
  });
}

function markAudioUnlocked() {
  if (!import.meta.client) return;
  try {
    sessionStorage.setItem(CHAT_AUDIO_UNLOCKED_KEY, '1');
  } catch {
    /* ignore */
  }
}

/**
 * 在用户手势（点击/触摸）回调内调用：解锁 Howler / WebAudio，满足 H5 自动播放策略。
 * 合并并发调用，避免同一次交互触发两遍静音解锁、进而干扰后续 TTS。
 */
export async function unlockWebAudio() {
  if (unlockWebAudioPromise) {
    return unlockWebAudioPromise;
  }
  unlockWebAudioPromise = (async () => {
    initMobileHandlers();
    Howler.autoUnlock = true;
    Howler.html5PoolSize = Math.max(Howler.html5PoolSize || 0, 10);
    try {
      if (Howler.ctx?.state === 'suspended') {
        await Howler.ctx.resume();
      }
    } catch (e) {
      console.warn('Howler.ctx.resume', e);
    }
    await new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      const dummy = new Howl({
        src: [config.silence],
        volume: 0,
        html5: shouldUseHtml5Audio(),
        onplay: () => {
          try {
            dummy.stop();
          } catch {
            /* ignore */
          }
          try {
            dummy.unload();
          } catch {
            /* ignore */
          }
          finish();
        },
        onloaderror: () => finish(),
        onplayerror: () => finish()
      });
      try {
        const id = dummy.play();
        if (!id) finish();
      } catch {
        finish();
      }
      setTimeout(finish, 600);
    });
    markAudioUnlocked();
  });
  try {
    return await unlockWebAudioPromise;
  } finally {
    unlockWebAudioPromise = null;
  }
}

export function bindAudioReady() {
  if (!import.meta.client) {
    return () => {};
  }

  if (globalAudioReadyCleanup) {
    return globalAudioReadyCleanup;
  }

  initMobileHandlers();

  async function ready() {
    await unlockWebAudio();
    markAudioUnlocked();
  }

  const pointerOpts = { once: true, passive: true, capture: true };
  const touchOpts = { once: true, passive: true };
  const clickOpts = { once: true, passive: true };

  document.addEventListener('pointerdown', ready, pointerOpts);
  document.addEventListener('touchstart', ready, touchOpts);
  document.addEventListener('click', ready, clickOpts);

  globalAudioReadyCleanup = () => {
    document.removeEventListener('pointerdown', ready, pointerOpts);
    document.removeEventListener('touchstart', ready, touchOpts);
    document.removeEventListener('click', ready, clickOpts);
    globalAudioReadyCleanup = null;
  };

  return globalAudioReadyCleanup;
}

export function primeChatAudioUnlock() {
  markAudioUnlocked();
  void unlockWebAudio().catch((error) => {
    console.warn('primeChatAudioUnlock', error);
  });
}

export function toggleMute(isMuted) {
  stablePlaybackMuted = isMuted;
  Howler.mute(isMuted);
  syncStablePlaybackGains();
}

export function toggleVolume(volume) {
  stablePlaybackVolume = volume;
  Howler.volume(volume);
  syncStablePlaybackGains();
}

function resolveSegmenterLocale(language) {
  switch (String(language || '').toLowerCase()) {
    case 'en':
    case 'en-us':
      return 'en-US'
    case 'th':
    case 'th-th':
      return 'th-TH'
    case 'zh':
    case 'zh-cn':
    default:
      return 'zh-CN'
  }
}


export class StreamAudioPlayer {
  constructor({
    errorCallback,
    onPlayStart,
    onPlayEnd,
    getConfigId = () => null,
    getThreadId = () => null,
    getLanguage = () => null
  } = {}) {
    this.audioQueue = [];
    this.pendingSentenceQueue = [];
    this.readyAudioMap = new Map();
    this.isPlaying = false;
    this.hasStartedPlayback = false;
    this.audioInitialized = false;
    this.currentHowl = null;
    this.currentBufferSource = null;
    this.outputGainNode = null;
    this.memoText = '';
    /** 防止同一段整句 processStream(text,true) 被连续调两次（双挂载 / 重复 resume） */
    this._oneShotTtsKey = null;
    this.activeFetchCount = 0;
    this.isDestroyed = false;
    this.inputEnded = false;
    this.playbackStartTimer = null;
    this.nextSentenceSeq = 0;
    this.nextPlayableSeq = 0;
    this.errorCallback = errorCallback;
    this.onPlayStart = onPlayStart;
    this.onPlayEnd = onPlayEnd;
    this.getConfigId = getConfigId;
    this.getThreadId = getThreadId;
    this.getLanguage = getLanguage;
    const { $myFetch } = useNuxtApp();
    this.$myFetch = $myFetch;
    this.useHtml5Audio = shouldUseHtml5Audio();
    this.isAndroid = isAndroidUserAgent();
    this.currentAudioUrl = null;

    // 初始化 Howler 全局配置
    Howler.autoUnlock = true;
    Howler.html5PoolSize = 10;
    Howler.usingWebAudio = !this.useHtml5Audio;

    this.maxConcurrentFetches = 2;
    this.startBufferSegments = this.isAndroid ? 5 : 3;
    this.startBufferDelayMs = this.isAndroid ? 800 : 450;

    this.initVolumeControl();
  }

  useStableAndroidBufferPlayback() {
    return this.isAndroid && !this.useHtml5Audio;
  }

  initVolumeControl() {
    // 设置适中的初始音量
    Howler.volume(0.6);
  }

  ensureOutputGainNode() {
    if (!this.useStableAndroidBufferPlayback()) {
      return null;
    }

    const ctx = Howler.ctx;
    if (!ctx) {
      return null;
    }

    if (!this.outputGainNode) {
      this.outputGainNode = ctx.createGain();
      this.outputGainNode.connect(ctx.destination);
      stablePlaybackGainNodes.add(this.outputGainNode);
    }

    this.outputGainNode.gain.value = stablePlaybackMuted ? 0 : stablePlaybackVolume;
    return this.outputGainNode;
  }

  async initialize() {
    if (this.audioInitialized) return;
    
    return new Promise((resolve) => {
      const dummy = new Howl({
        src: [config.silence],
        volume: 0,
        onplay: () => {
          dummy.stop();
          this.audioInitialized = true;
          resolve();
        }
      });
      dummy.play();
    });
  }

  async processStream(text, isEnd) {
    if (this.isDestroyed || (!text && !isEnd)) return;
    if (isEnd) {
      this.inputEnded = true;
    }
    if (isEnd && text && this.memoText === '') {
      if (this._oneShotTtsKey === text) {
        return;
      }
      this._oneShotTtsKey = text;
    }
    this.memoText += normalizeTtsTextChunk(text);
    const { segments, remainder } = extractSpeakableSegments(this.memoText, isEnd, this.getLanguage?.());
    this.memoText = remainder;
    const normalizedSegments = this.isAndroid
      ? mergeShortSegments(segments, 32, 140)
      : segments;
    if (normalizedSegments.length) {
      this.pendingSentenceQueue.push(...normalizedSegments.map(textSegment => ({
        seq: this.nextSentenceSeq++,
        text: textSegment
      })));
    }
    // 本轮结束后释放去重键，避免之后合法重复同一句无法播放
    if (isEnd && text) {
      const dedupeKey = text;
      queueMicrotask(() => {
        if (!this.isDestroyed && this._oneShotTtsKey === dedupeKey) {
          this._oneShotTtsKey = null;
        }
      });
    }
    if (!this.isDestroyed) {
      this.pumpFetchQueue();
      this.maybeStartPlayback();
    }
  }

  pumpFetchQueue() {
    while (
      !this.isDestroyed
      && this.activeFetchCount < this.maxConcurrentFetches
      && this.pendingSentenceQueue.length
    ) {
      this.fetchNextTTS();
    }
  }

  async fetchNextTTS() {
    if (this.isDestroyed || !this.pendingSentenceQueue.length) {
      return;
    }
    this.activeFetchCount += 1;
    const sentence = this.pendingSentenceQueue.shift();
    try {
      const audioData = await this.fetchTTS(sentence.text);
      if (!this.isDestroyed) {
        const playbackData = this.useStableAndroidBufferPlayback()
          ? await this.decodeBase64AudioToBuffer(audioData)
          : audioData;
        this.enqueueAudio(sentence.seq, playbackData);
      }
    } catch {
      if (!this.isDestroyed) {
        this.errorCallback?.();
        console.log('获取TTS转换语音失败：', sentence.text);
      }
    } finally {
      if (!this.isDestroyed) {
        this.activeFetchCount = Math.max(0, this.activeFetchCount - 1);
        this.pumpFetchQueue();
        this.maybeStartPlayback();
      }
    }
  }

  fetchTTS(text, retryTimes = 0) {
    if (this.isDestroyed) return Promise.reject(new Error('Player destroyed'));
    
    return new Promise((resolve, reject) => {
      try {
        const payload = {
          text,
          speed_ratio: 1,
          volume_ratio: 1,
          pitch_ratio: 1
        };
        const configId = this.getConfigId?.();
        const threadId = this.getThreadId?.();
        const language = this.getLanguage?.();
        if (configId !== undefined && configId !== null) {
          payload.config_id = configId;
        }
        if (threadId !== undefined && threadId !== null && threadId !== '') {
          payload.thread_id = threadId;
        }
        if (language) {
          payload.language = language;
        }
        this.$myFetch(`/tts`, {
          method: 'POST',
          body: JSON.stringify(payload)
        }).then(data => {
          if (!this.isDestroyed) {
            resolve(data.data);
          } else {
            reject(new Error('Player destroyed'));
          }
        }).catch(_e => {
          if (retryTimes < 3 && !this.isDestroyed) {
            resolve(this.fetchTTS(text, retryTimes + 1));
          } else {
            reject(_e);
          }
        });
      } catch(_e) {
        reject(_e);
      }
    });
  }

  enqueueAudio(seq, data) {
    if (this.isDestroyed) return;
    this.readyAudioMap.set(seq, data);
    while (this.readyAudioMap.has(this.nextPlayableSeq)) {
      this.audioQueue.push(this.readyAudioMap.get(this.nextPlayableSeq));
      this.readyAudioMap.delete(this.nextPlayableSeq);
      this.nextPlayableSeq += 1;
    }
    this.maybeStartPlayback();
  }

  async decodeBase64AudioToBuffer(base64) {
    const ctx = Howler.ctx;
    if (!ctx) {
      throw new Error('Howler audio context unavailable');
    }

    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    const binary = window.atob(base64);
    const length = binary.length;
    const bytes = new Uint8Array(length);
    for (let i = 0; i < length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }

    const arrayBuffer = bytes.buffer.slice(0);
    return await new Promise((resolve, reject) => {
      ctx.decodeAudioData(
        arrayBuffer,
        buffer => resolve(buffer),
        error => reject(error)
      );
    });
  }

  shouldStartPlayback() {
    if (!this.audioQueue.length) return false;
    if (this.hasStartedPlayback) return true;
    if (this.audioQueue.length >= this.startBufferSegments) return true;
    if (this.inputEnded && this.activeFetchCount === 0 && this.pendingSentenceQueue.length === 0) return true;
    return false;
  }

  maybeStartPlayback() {
    if (this.isDestroyed || this.isPlaying) return;

    if (this.shouldStartPlayback()) {
      this.clearPlaybackStartTimer();
      this.playNext();
      return;
    }

    if (!this.playbackStartTimer && this.audioQueue.length > 0) {
      this.playbackStartTimer = setTimeout(() => {
        this.playbackStartTimer = null;
        if (!this.isDestroyed && !this.isPlaying && this.audioQueue.length > 0) {
          this.playNext();
        }
      }, this.startBufferDelayMs);
    }
  }

  clearPlaybackStartTimer() {
    if (this.playbackStartTimer) {
      clearTimeout(this.playbackStartTimer);
      this.playbackStartTimer = null;
    }
  }

  async playNext() {
    if (this.isDestroyed || this.isPlaying || !this.audioQueue.length) return;

    if (this.useStableAndroidBufferPlayback()) {
      await this.playNextBuffer();
      return;
    }

    this.isPlaying = true;
    this.hasStartedPlayback = true;
    this.clearPlaybackStartTimer();
    const data = this.audioQueue.shift();

    try {
      this.revokeCurrentAudioUrl();
      const src = this.isAndroid
        ? base64ToObjectUrl(data)
        : `data:audio/wav;base64,${data}`;
      this.currentAudioUrl = this.isAndroid ? src : null;

      this.currentHowl = new Howl({
        src: [src],
        format: ['wav'],
        html5: this.useHtml5Audio,
        preload: true,
        onend: () => {
          if (!this.isDestroyed) {
            this.isPlaying = false;
            this.currentHowl?.unload();
            this.revokeCurrentAudioUrl();
            this.maybeStartPlayback();
            this.onPlayEnd?.();
          }
        },
        onplayerror: () => this.handlePlayError(),
        onloaderror: () => this.handlePlayError()
      });

      await new Promise((resolve, reject) => {
        if (this.isDestroyed) {
          reject(new Error('Player destroyed'));
          return;
        }
        this.currentHowl.once('play', resolve);
        this.currentHowl.once('loaderror', reject);
        this.currentHowl.play();
      });
      this.onPlayStart?.();
    } catch (err) {
      if (!this.isDestroyed) {
        console.error('播放出错:', err);
        this.handlePlayError();
      }
      this.onPlayEnd?.();
    }
  }

  async playNextBuffer() {
    if (this.isDestroyed || this.isPlaying || !this.audioQueue.length) return;

    this.isPlaying = true;
    this.hasStartedPlayback = true;
    this.clearPlaybackStartTimer();

    const audioBuffer = this.audioQueue.shift();

    try {
      const ctx = Howler.ctx;
      const gainNode = this.ensureOutputGainNode();
      if (!ctx || !gainNode || !audioBuffer) {
        throw new Error('Android WebAudio queue unavailable');
      }

      if (ctx.state === 'suspended') {
        await ctx.resume();
      }

      this.currentBufferSource = ctx.createBufferSource();
      this.currentBufferSource.buffer = audioBuffer;
      this.currentBufferSource.connect(gainNode);
      this.currentBufferSource.onended = () => {
        if (!this.isDestroyed) {
          this.isPlaying = false;
          this.currentBufferSource?.disconnect();
          this.currentBufferSource = null;
          this.maybeStartPlayback();
          this.onPlayEnd?.();
        }
      };

      this.currentBufferSource.start(0);
      this.onPlayStart?.();
    } catch (err) {
      if (!this.isDestroyed) {
        console.error('Android WebAudio 播放出错:', err);
        this.handlePlayError();
      }
      this.onPlayEnd?.();
    }
  }

  handlePlayError() {
    if (this.isDestroyed) return;
    console.error('播放失败，尝试格式回退');
    this.onPlayEnd?.();
    this.isPlaying = false;
    try {
      this.currentBufferSource?.stop();
    } catch {
      /* ignore */
    }
    this.currentBufferSource?.disconnect?.();
    this.currentBufferSource = null;
    this.currentHowl?.unload();
    this.currentHowl = null;
    this.revokeCurrentAudioUrl();
    this.maybeStartPlayback();
  }

  revokeCurrentAudioUrl() {
    if (!this.currentAudioUrl) return;
    try {
      URL.revokeObjectURL(this.currentAudioUrl);
    } catch {
      /* ignore */
    }
    this.currentAudioUrl = null;
  }

  destroy() {
    this.onPlayEnd?.();
    this.isDestroyed = true;
    this.clearPlaybackStartTimer();
    this.pendingSentenceQueue = [];
    this.audioQueue = [];
    this.readyAudioMap.clear();
    try {
      this.currentBufferSource?.stop();
    } catch {
      /* ignore */
    }
    this.currentBufferSource?.disconnect?.();
    this.currentBufferSource = null;
    this.currentHowl?.unload();
    this.revokeCurrentAudioUrl();
    this.isPlaying = false;
    this.hasStartedPlayback = false;
    this.activeFetchCount = 0;
    this.inputEnded = false;
    this.nextSentenceSeq = 0;
    this.nextPlayableSeq = 0;
    this.memoText = '';
    this._oneShotTtsKey = null;
    this.currentHowl = null;
    if (this.outputGainNode) {
      try {
        this.outputGainNode.disconnect();
      } catch {
        /* ignore */
      }
      stablePlaybackGainNodes.delete(this.outputGainNode);
      this.outputGainNode = null;
    }
  }
}

export function unloadTTS() {
  Howler.unload();
}


const STRONG_SENTENCE_END_CHARS = new Set(['。', '！', '？', '!', '?', '；', ';', '…'])
const SENTENCE_CLOSING_CHARS = new Set(['"', '\'', '”', '’', ')', '）', ']', '】', '》'])
const CLAUSE_SPLIT_CHARS = ['；', ';', '：', '，', ',', '、']
const TTS_SOFT_SENTENCE_LENGTH = 60
const TTS_HARD_SENTENCE_LENGTH = 110
const segmenterCache = new Map()

function getSegmenter(language, granularity) {
  if (typeof Intl === 'undefined' || typeof Intl.Segmenter === 'undefined') {
    return null
  }

  const locale = resolveSegmenterLocale(language)
  const key = `${locale}:${granularity}`
  if (!segmenterCache.has(key)) {
    segmenterCache.set(key, new Intl.Segmenter(locale, { granularity }))
  }

  return segmenterCache.get(key)
}

function normalizeTtsTextChunk(text) {
  return text
    .replace(/```[\w-]*\n?/g, '')
    .replace(/```/g, '')
    .replace(/`+/g, '')
    .replace(/\*\*/g, '')
    .replace(/#{1,6}\s*/g, '')
    .replace(/(^|[\n\r])\s*\[[^\]]{1,12}\]\s*/g, '$1')
    .replace(/\r\n/g, '\n')
    .replace(/\u00a0/g, ' ')
}

function extractSpeakableSegments(text, isEnd, language) {
  const normalizedText = text
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim()

  if (!normalizedText) {
    return {
      segments: [],
      remainder: ''
    }
  }

  const rawSegments = segmentTextBySentence(normalizedText, language)
  const completedSentences = []
  let remainder = ''

  rawSegments.forEach((segment, index) => {
    const normalizedSegment = segment.replace(/\s+/g, ' ').trim()
    if (!normalizedSegment) return

    const isLastSegment = index === rawSegments.length - 1
    if (!isLastSegment || isEnd || isSentenceClosed(normalizedSegment)) {
      completedSentences.push(normalizedSegment)
      return
    }

    remainder = normalizedSegment
  })

  if (isEnd && remainder) {
    completedSentences.push(remainder)
    remainder = ''
  }

  const segments = []
  for (const completedSentence of completedSentences) {
    segments.push(...splitCompletedSentenceForTts(completedSentence, language))
  }

  return {
    segments: mergeShortSegments(segments),
    remainder
  }
}

function splitSentenceByPunctuation(text) {
  const sentences = []
  let current = ''

  for (let i = 0; i < text.length; i++) {
    const char = text[i]
    current += char

    const isStrongSentenceEnd = STRONG_SENTENCE_END_CHARS.has(char) || (char === '.' && hasTrailingEllipsis(text, i))
    if (!isStrongSentenceEnd) {
      continue
    }

    while (i + 1 < text.length) {
      const nextChar = text[i + 1]
      if (!isSentenceTrailingChar(nextChar)) {
        break
      }
      current += nextChar
      i += 1
    }

    sentences.push(current)
    current = ''
  }

  if (current) {
    sentences.push(current)
  }

  return sentences
}

function segmentTextBySentence(text, language) {
  const sentenceSegmenter = getSegmenter(language, 'sentence')
  if (sentenceSegmenter) {
    return Array.from(sentenceSegmenter.segment(text), item => item.segment)
  }

  return splitSentenceByPunctuation(text)
}

function isSentenceTrailingChar(char) {
  return STRONG_SENTENCE_END_CHARS.has(char) || SENTENCE_CLOSING_CHARS.has(char)
}

function isSentenceClosed(text) {
  const normalizedText = stripTrailingSentenceClosers(text)
  if (!normalizedText) return false

  const lastChar = normalizedText[normalizedText.length - 1]
  if (STRONG_SENTENCE_END_CHARS.has(lastChar)) {
    return true
  }

  if (lastChar === '.') {
    return hasTrailingEllipsis(normalizedText, normalizedText.length - 1)
  }

  return false
}

function hasTrailingEllipsis(text, index) {
  let dotCount = 0
  let cursor = index

  while (cursor >= 0 && text[cursor] === '.') {
    dotCount += 1
    cursor -= 1
  }

  return dotCount >= 3
}

function stripTrailingSentenceClosers(text) {
  let normalizedText = text.trim()

  while (normalizedText) {
    const lastChar = normalizedText[normalizedText.length - 1]
    if (!SENTENCE_CLOSING_CHARS.has(lastChar)) {
      break
    }
    normalizedText = normalizedText.slice(0, -1).trimEnd()
  }

  return normalizedText
}

function splitCompletedSentenceForTts(sentence, language) {
  const normalizedSentence = sentence.replace(/\s+/g, ' ').trim()
  if (!normalizedSentence) return []
  if (normalizedSentence.length <= TTS_HARD_SENTENCE_LENGTH) {
    return [normalizedSentence]
  }

  return splitLongSentence(normalizedSentence, language)
}

function findWordBoundarySplit(sentence, language, targetIndex, hardLimit) {
  const wordSegmenter = getSegmenter(language, 'word')
  if (!wordSegmenter) return -1

  let splitPos = -1
  let minDistance = Number.POSITIVE_INFINITY
  for (const part of wordSegmenter.segment(sentence)) {
    const candidatePos = part.index + part.segment.length
    if (candidatePos < Math.max(16, Math.floor(TTS_SOFT_SENTENCE_LENGTH * 0.5))) continue
    if (candidatePos > hardLimit) continue

    const distance = Math.abs(candidatePos - targetIndex)
    if (distance < minDistance) {
      minDistance = distance
      splitPos = candidatePos
    }
  }

  return splitPos
}

function splitLongSentence(sentence, language, softLimit = TTS_SOFT_SENTENCE_LENGTH, hardLimit = TTS_HARD_SENTENCE_LENGTH) {
  if (sentence.length <= hardLimit) {
    return [sentence]
  }

  const targetIndex = Math.min(sentence.length - 1, softLimit)
  let splitPos = -1
  let minDistance = Number.POSITIVE_INFINITY

  for (let i = 0; i < sentence.length; i++) {
    if (!CLAUSE_SPLIT_CHARS.includes(sentence[i])) continue

    const candidatePos = i + 1
    const distance = Math.abs(candidatePos - targetIndex)

    if (candidatePos < Math.max(16, Math.floor(softLimit * 0.5))) continue
    if (candidatePos > hardLimit) continue

    if (distance < minDistance) {
      minDistance = distance
      splitPos = candidatePos
    }
  }

  if (splitPos === -1) {
    splitPos = findWordBoundarySplit(sentence, language, targetIndex, hardLimit)
  }

  if (splitPos === -1) {
    splitPos = hardLimit
  }

  const current = sentence.slice(0, splitPos).trim()
  const remaining = sentence.slice(splitPos).trim()

  if (!remaining) {
    return current ? [current] : []
  }

  return current
    ? [current, ...splitLongSentence(remaining, language, softLimit, hardLimit)]
    : splitLongSentence(remaining, language, softLimit, hardLimit)
}

function mergeShortSegments(segments, minSegmentLength = 10, maxMergedLength = 40) {
  const merged = []

  for (const segment of segments) {
    if (!segment) continue

    const previous = merged[merged.length - 1]
    if (
      previous &&
      previous.length < minSegmentLength &&
      previous.length + segment.length <= maxMergedLength &&
      !/[。！？!?；;…]$/.test(previous)
    ) {
      merged[merged.length - 1] = `${previous}${segment}`.trim()
      continue
    }

    merged.push(segment)
  }

  return merged
}

// /* 测试用例 */
// const testText = "这是一个测试长文本。它包含多个句子！有些句子会特别特别长，比如这个超过25个字符的句子需要通过智能分割来处理；而另一些则可能包含复杂标点，例如：顿号、分号等情况……最后还要处理没有标点的超长字符串这种情况。";

// console.log(splitChineseSentences(testText));
